import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FileText, Truck, ShoppingCart, Search, Filter, Download,
  ChevronDown, Calendar, Eye, ArrowUpDown, CheckCircle2, X,
  EuroIcon
} from 'lucide-react';
import { factures, bonsCommande, bonsLivraison, formatMontant, formatDate, getStatutLabel, getStatutColor } from '../data/mockData';

const Documents = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'factures';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [searchTerm, setSearchTerm] = useState('');
  const [statutFilter, setStatutFilter] = useState('tous');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab) setActiveTab(tab);
  }, [searchParams]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
    setSearchTerm('');
    setStatutFilter('tous');
  };

  const tabs = [
    { id: 'factures', label: 'Factures', icon: FileText, count: factures.length },
    { id: 'commandes', label: 'Bons de commande', icon: ShoppingCart, count: bonsCommande.length },
    { id: 'livraisons', label: 'Bons de livraison', icon: Truck, count: bonsLivraison.length },
    { id: 'tarifs', label: 'Mes tarifs', icon: EuroIcon, count: 1 },
  ];

  const getDocuments = () => {
    let docs = [];
    switch (activeTab) {
      case 'factures': docs = factures; break;
      case 'commandes': docs = bonsCommande; break;
      case 'livraisons': docs = bonsLivraison; break;
      default: docs = factures;
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      docs = docs.filter(d => 
        d.id.toLowerCase().includes(term) || 
        d.reference.toLowerCase().includes(term)
      );
    }

    if (statutFilter !== 'tous') {
      docs = docs.filter(d => d.statut === statutFilter);
    }

    return docs;
  };

  const documents = getDocuments();

  const getStatuts = () => {
    switch (activeTab) {
      case 'factures': return ['tous', 'en_attente', 'en_retard', 'payee'];
      case 'commandes': return ['tous', 'en_cours', 'livre'];
      case 'livraisons': return ['tous', 'livre'];
      default: return ['tous'];
    }
  };

  const renderFactureRow = (doc) => {
    const statutStyle = getStatutColor(doc.statut);
    return (
      <tr key={doc.id} className="table-row-clickable" onClick={() => navigate(`/documents/${doc.id}`)}>
        <td>
          <div className="doc-cell-id">
            <FileText size={16} className="doc-cell-icon facture-color" />
            <span className="mono-text">{doc.id}</span>
          </div>
        </td>
        <td>{formatDate(doc.date)}</td>
        <td className="doc-cell-ref">{doc.reference}</td>
        <td style={{ textAlign: 'right' }}>{formatMontant(doc.montantHT)}</td>
        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(doc.montantTTC)}</td>
        <td>{formatDate(doc.echeance)}</td>
        <td>
          <span className="doc-status-badge" style={{ background: statutStyle.bg, color: statutStyle.color, borderColor: statutStyle.border }}>
            {getStatutLabel(doc.statut)}
          </span>
        </td>
        <td>
          <div className="doc-actions">
            <button className="btn-icon-sm" title="Voir"><Eye size={15} /></button>
            <button className="btn-icon-sm" title="Télécharger PDF"><Download size={15} /></button>
          </div>
        </td>
      </tr>
    );
  };

  const renderCommandeRow = (doc) => {
    const statutStyle = getStatutColor(doc.statut);
    return (
      <tr key={doc.id} className="table-row-clickable" onClick={() => navigate(`/documents/${doc.id}`)}>
        <td>
          <div className="doc-cell-id">
            <ShoppingCart size={16} className="doc-cell-icon commande-color" />
            <span className="mono-text">{doc.id}</span>
          </div>
        </td>
        <td>{formatDate(doc.date)}</td>
        <td className="doc-cell-ref">{doc.reference}</td>
        <td>{doc.carriere}</td>
        <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatMontant(doc.montantTTC)}</td>
        <td>
          <span className="doc-status-badge" style={{ background: statutStyle.bg, color: statutStyle.color, borderColor: statutStyle.border }}>
            {getStatutLabel(doc.statut)}
          </span>
        </td>
        <td>
          <div className="doc-actions">
            <button className="btn-icon-sm" title="Voir"><Eye size={15} /></button>
            <button className="btn-icon-sm" title="Télécharger PDF"><Download size={15} /></button>
          </div>
        </td>
      </tr>
    );
  };

  const renderLivraisonRow = (doc) => {
    const statutStyle = getStatutColor(doc.statut);
    return (
      <tr key={doc.id} className="table-row-clickable" onClick={() => navigate(`/documents/${doc.id}`)}>
        <td>
          <div className="doc-cell-id">
            <Truck size={16} className="doc-cell-icon livraison-color" />
            <span className="mono-text">{doc.id}</span>
          </div>
        </td>
        <td>{formatDate(doc.date)}</td>
        <td className="doc-cell-ref">{doc.reference}</td>
        <td>{doc.carriere}</td>
        <td>{doc.chauffeur}</td>
        <td>{doc.adresseLivraison}</td>
        <td>
          <span className="doc-status-badge" style={{ background: statutStyle.bg, color: statutStyle.color, borderColor: statutStyle.border }}>
            <CheckCircle2 size={12} /> {getStatutLabel(doc.statut)}
          </span>
        </td>
        <td>
          <div className="doc-actions">
            <button className="btn-icon-sm" title="Voir"><Eye size={15} /></button>
            <button className="btn-icon-sm" title="Télécharger PDF"><Download size={15} /></button>
          </div>
        </td>
      </tr>
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Mes documents</h1>
          <p className="page-subtitle">Consultez et téléchargez vos factures, bons de commande et bons de livraison</p>
        </div>
      </div>

      {/* Onglets */}
      <div className="doc-tabs">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              className={`doc-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => handleTabChange(tab.id)}
            >
              <Icon size={18} />
              <span>{tab.label}</span>
              <span className="doc-tab-count">{tab.count}</span>
            </button>
          );
        })}
      </div>

      {/* Barre de recherche et filtres */}
      <div className="doc-toolbar">
        <div className="doc-search">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher par n° ou référence..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="search-clear" onClick={() => setSearchTerm('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="doc-toolbar-right">
          <div className="doc-filter-group">
            <label>Statut :</label>
            <select value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}>
              {getStatuts().map(s => (
                <option key={s} value={s}>{s === 'tous' ? 'Tous' : getStatutLabel(s)}</option>
              ))}
            </select>
          </div>
          <button className="btn-secondary">
            <Download size={16} /> Tout exporter
          </button>
        </div>
      </div>

      {/* Tableau */}
      <div className="doc-table-container">
        {documents.length === 0 ? (
          <div className="empty-state">
            <FileText size={48} strokeWidth={1} />
            <h3>Aucun document trouvé</h3>
            <p>Modifiez vos critères de recherche ou filtres</p>
          </div>
        ) : (
          <table className="data-table">
            <thead>
              {activeTab === 'factures' && (
                <tr>
                  <th>N° Facture</th>
                  <th>Date</th>
                  <th>Référence chantier</th>
                  <th style={{ textAlign: 'right' }}>Montant HT</th>
                  <th style={{ textAlign: 'right' }}>Montant TTC</th>
                  <th>Échéance</th>
                  <th>Statut</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              )}
              {activeTab === 'commandes' && (
                <tr>
                  <th>N° BC</th>
                  <th>Date</th>
                  <th>Référence chantier</th>
                  <th>Carrière</th>
                  <th style={{ textAlign: 'right' }}>Montant TTC</th>
                  <th>Statut</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              )}
              {activeTab === 'livraisons' && (
                <tr>
                  <th>N° BL</th>
                  <th>Date</th>
                  <th>Référence chantier</th>
                  <th>Carrière</th>
                  <th>Chauffeur</th>
                  <th>Adresse livraison</th>
                  <th>Statut</th>
                  <th style={{ width: '80px' }}></th>
                </tr>
              )}
            </thead>
            <tbody>
              {activeTab === 'factures' && documents.map(renderFactureRow)}
              {activeTab === 'commandes' && documents.map(renderCommandeRow)}
              {activeTab === 'livraisons' && documents.map(renderLivraisonRow)}
            </tbody>
          </table>
        )}
      </div>

      <div className="doc-footer">
        <span>{documents.length} document{documents.length > 1 ? 's' : ''}</span>
      </div>
    </div>
  );
};

export default Documents;
